type SalsaPrimitive = string | number | boolean | null | undefined;
export type SalsaProperties = Record<string, SalsaPrimitive | SalsaPrimitive[]>;

type SalsaMethod = 'track' | 'page' | 'identify';

interface SalsaSdk {
  init?: (config: Record<string, unknown>) => void | Promise<void>;
  track?: (eventName: string, properties?: SalsaProperties) => void | Promise<void>;
  capture?: (eventName: string, properties?: SalsaProperties) => void | Promise<void>;
  page?: (properties?: SalsaProperties) => void | Promise<void>;
  identify?: (visitorId: string, properties?: SalsaProperties) => void | Promise<void>;
}

type SalsaFactory = (clientKey: string, options?: Record<string, unknown>) => SalsaSdk | Promise<SalsaSdk>;

interface QueuedSalsaCall {
  method: SalsaMethod;
  args: [string | SalsaProperties, SalsaProperties?];
  queuedAt: string;
}

declare global {
  interface Window {
    Salsa?: SalsaSdk | SalsaFactory;
    SalsaAnalytics?: SalsaSdk;
    salsaAnalytics?: SalsaSdk;
    salsa?: SalsaSdk | { analytics?: SalsaSdk };
    __onzoeSalsaAnalyticsQueue?: QueuedSalsaCall[];
    __onzoeSalsaAnalyticsInitialized?: boolean;
  }
}

const env = import.meta.env;

const analyticsConfig = {
  enabled: env.VITE_SALSA_ANALYTICS_ENABLED !== 'false',
  requireConsent: env.VITE_SALSA_ANALYTICS_REQUIRE_CONSENT !== 'false',
  sdkUrl: env.VITE_SALSA_ANALYTICS_SDK_URL || 'https://cloud.salsas.io/t/sdk.js',
  clientKey: env.VITE_SALSA_ANALYTICS_CLIENT_KEY || env.VITE_SALSA_ANALYTICS_WRITE_KEY || 'onzoe-marketing',
  teamKey: env.VITE_SALSA_ANALYTICS_TEAM_KEY || 'ONZOE',
  source: env.VITE_SALSA_ANALYTICS_SOURCE || 'onzoe-marketing-site',
  environment: env.VITE_SALSA_ANALYTICS_ENV || (env.PROD ? 'production' : 'development'),
  debug: env.VITE_SALSA_ANALYTICS_DEBUG === 'true',
};

const ATTRIBUTION_STORAGE_KEY = 'onzoe-first-touch-attribution';
const ATTRIBUTION_KEYS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
  'onzoe_campaign', 'onzoe_asset', 'onzoe_click',
] as const;

export type CampaignAttribution = Partial<Record<(typeof ATTRIBUTION_KEYS)[number], string>> & {
  landing_url?: string;
  referrer?: string;
};

export const captureCampaignAttribution = (): CampaignAttribution => {
  if (typeof window === 'undefined') return {};
  let stored: CampaignAttribution = {};
  try {
    stored = JSON.parse(window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY) || '{}') as CampaignAttribution;
  } catch {
    stored = {};
  }
  const params = new URLSearchParams(window.location.search);
  const observed: CampaignAttribution = {};
  ATTRIBUTION_KEYS.forEach((key) => {
    const value = params.get(key)?.trim();
    if (value) observed[key] = value.slice(0, 180);
  });
  if (Object.keys(observed).length === 0) return stored;
  const next: CampaignAttribution = {
    ...stored,
    ...observed,
    landing_url: stored.landing_url || window.location.href,
    referrer: stored.referrer || document.referrer || undefined,
  };
  try {
    window.localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // The active page still carries attribution if storage is unavailable.
  }
  return next;
};

export const getCampaignAttribution = (): CampaignAttribution => captureCampaignAttribution();

let loadPromise: Promise<boolean> | null = null;
let initializedSdk: SalsaSdk | null = null;

const logDebug = (...args: unknown[]) => {
  if (analyticsConfig.debug) {
    console.debug('[Salsa analytics]', ...args);
  }
};

const readCookieSettings = () => {
  try {
    const rawSettings = window.localStorage.getItem('cookieSettings');
    return rawSettings ? JSON.parse(rawSettings) as { analytics?: boolean } : null;
  } catch {
    return null;
  }
};

export const hasSalsaAnalyticsConsent = () => {
  if (typeof window === 'undefined') return false;
  if (!analyticsConfig.requireConsent) return true;

  const consent = window.localStorage.getItem('cookieConsent');
  if (consent === 'accepted') return true;
  if (consent === 'declined') return false;

  return readCookieSettings()?.analytics === true;
};

const isSdk = (candidate: unknown): candidate is SalsaSdk => {
  if (!candidate || typeof candidate !== 'object') return false;

  const sdk = candidate as SalsaSdk;
  return Boolean(sdk.init || sdk.track || sdk.capture || sdk.page || sdk.identify);
};

const getLoadedSdk = (): SalsaSdk | null => {
  if (initializedSdk) return initializedSdk;
  if (isSdk(window.SalsaAnalytics)) return window.SalsaAnalytics;
  if (isSdk(window.salsaAnalytics)) return window.salsaAnalytics;
  if (isSdk(window.salsa)) return window.salsa;
  if (window.salsa && 'analytics' in window.salsa && isSdk(window.salsa.analytics)) return window.salsa.analytics;
  if (isSdk(window.Salsa)) return window.Salsa;
  return null;
};

const initializeLoadedSdk = async () => {
  if (window.__onzoeSalsaAnalyticsInitialized) return getLoadedSdk();

  if (typeof window.Salsa === 'function') {
    initializedSdk = await window.Salsa(analyticsConfig.clientKey, {
      env: analyticsConfig.environment,
      teamKey: analyticsConfig.teamKey,
      source: analyticsConfig.source,
    });
  } else {
    initializedSdk = getLoadedSdk();
    await initializedSdk?.init?.({
      clientKey: analyticsConfig.clientKey,
      teamKey: analyticsConfig.teamKey,
      source: analyticsConfig.source,
      environment: analyticsConfig.environment,
    });
  }

  window.__onzoeSalsaAnalyticsInitialized = true;
  return getLoadedSdk();
};

export const loadSalsaAnalytics = () => {
  if (!analyticsConfig.enabled || !hasSalsaAnalyticsConsent()) return Promise.resolve(false);
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve) => {
    const finish = async (loaded: boolean) => {
      try {
        await initializeLoadedSdk();
        resolve(loaded || Boolean(getLoadedSdk()));
      } catch (error) {
        logDebug('Initialization failed', error);
        resolve(false);
      }
    };

    if (getLoadedSdk() || !analyticsConfig.sdkUrl) {
      void finish(Boolean(getLoadedSdk()));
      return;
    }

    const existingScript = document.querySelector<HTMLScriptElement>('script[data-onzoe-salsa-analytics="true"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => void finish(true), { once: true });
      existingScript.addEventListener('error', () => resolve(false), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.defer = true;
    script.src = analyticsConfig.sdkUrl;
    script.dataset.onzoeSalsaAnalytics = 'true';
    // Salsa Analytics SDK v2 contract: data-site-id "<TEAM_KEY>:<property-slug>" is required.
    script.dataset.siteId = `${analyticsConfig.teamKey}:${analyticsConfig.clientKey}`;
    script.dataset.source = analyticsConfig.source;
    script.dataset.assetType = 'website';
    script.dataset.assetId = 'onzoe.com';
    script.addEventListener('load', () => void finish(true), { once: true });
    script.addEventListener('error', () => {
      logDebug('SDK script failed to load', analyticsConfig.sdkUrl);
      resolve(false);
    }, { once: true });
    document.head.appendChild(script);
  });

  return loadPromise;
};

const queueCall = (method: SalsaMethod, args: [string | SalsaProperties, SalsaProperties?]) => {
  window.__onzoeSalsaAnalyticsQueue = window.__onzoeSalsaAnalyticsQueue || [];
  window.__onzoeSalsaAnalyticsQueue.push({
    method,
    args,
    queuedAt: new Date().toISOString(),
  });
};

const dispatch = async (method: SalsaMethod, args: [string | SalsaProperties, SalsaProperties?]) => {
  if (!analyticsConfig.enabled || !hasSalsaAnalyticsConsent()) return;

  await loadSalsaAnalytics();
  const sdk = getLoadedSdk();

  if (!sdk) {
    queueCall(method, args);
    return;
  }

  if (method === 'track') {
    const [eventName, properties] = args as [string, SalsaProperties?];
    if (sdk.track) {
      await sdk.track(eventName, properties);
      return;
    }
    if (sdk.capture) {
      await sdk.capture(eventName, properties);
      return;
    }
  }

  if (method === 'page') {
    if (sdk.page) {
      const [properties] = args as [SalsaProperties];
      await sdk.page(properties);
    }
    // Salsa SDK v2 has no page(); it auto-tracks SPA pageviews via the history API.
    return;
  }

  if (method === 'identify' && sdk.identify) {
    const [visitorId, properties] = args as [string, SalsaProperties?];
    await sdk.identify(visitorId, properties);
    return;
  }

  queueCall(method, args);
};

export const salsaAnalytics = {
  config: analyticsConfig,
  load: loadSalsaAnalytics,
  page: (properties: SalsaProperties) => {
    void dispatch('page', [properties]);
  },
  track: (eventName: string, properties: SalsaProperties = {}) => {
    void dispatch('track', [eventName, properties]);
  },
  identify: (visitorId: string, properties: SalsaProperties = {}) => {
    void dispatch('identify', [visitorId, properties]);
  },
};
